// client/src/components/pages/LexiStar/Messages/MessageDetail.jsx
//
// One message in full (FR-NTF-008): body and buttons as sent, recipient and
// address, delivery state and time, attempts and error, cost with its rate
// card, what it was about (trip / deviation / event / rule) and the fallback
// chain when a channel failed and the next one was tried.

import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { FaTimes } from 'react-icons/fa';
import { fmtDateTime } from '../StatusBoard/format';
import { getMessage, pickObject, pickRows } from '../NotificationRules/notificationsApi';
import { StateChip, ChannelBadge } from './MessageAtoms';
import { buttonsOf, intentLabel, recipientText, fmtZar, messageCost } from './messageMeta';

function Row({ label, children }) {
  return (
    <div className="grid grid-cols-3 gap-3 py-1.5 text-sm">
      <dt className="text-slate-500">{label}</dt>
      <dd className="col-span-2 text-slate-900 break-words">{children}</dd>
    </div>
  );
}

export default function MessageDetail({ message, onClose }) {
  const [detail, setDetail] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let alive = true;
    setDetail(null);
    setError(null);
    getMessage(message.id)
      .then((res) => alive && setDetail(res))
      .catch(
        (err) => alive && setError(err.response?.data?.error || 'Could not load the message.')
      );
    return () => {
      alive = false;
    };
  }, [message.id]);

  const m = { ...message, ...(pickObject(detail, 'message') || {}) };
  const chain = pickRows(detail, 'chain', 'fallbacks', 'fallback_chain').filter(
    (x) => x.id !== m.id
  );
  const cost = messageCost(m);
  const subject = m.subject || {};

  return (
    <div
      className="fixed inset-0 z-40 flex justify-end bg-black/40"
      onClick={onClose}
      role="presentation"
    >
      <aside
        role="dialog"
        aria-modal="true"
        aria-label="Message detail"
        className="bg-surface w-full max-w-lg h-full overflow-y-auto shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 bg-surface px-6 py-4 border-b border-slate-200 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <ChannelBadge channel={m.channel} />
            <StateChip state={m.state} />
          </div>
          <button
            onClick={onClose}
            aria-label="Close"
            className="p-2 text-slate-400 hover:text-slate-600"
          >
            <FaTimes size={16} />
          </button>
        </div>
        <div className="px-6 py-4 space-y-4">
          {error && (
            <p role="alert" className="text-sm text-red-700">
              {error}
            </p>
          )}
          <div
            className={`rounded-2xl px-3 py-2 ${m.direction === 'in' ? 'bg-slate-100' : 'bg-primary-50 ring-1 ring-primary-100'}`}
          >
            <p className="whitespace-pre-wrap text-sm text-slate-900">
              {m.body || <em className="text-slate-400">(no text)</em>}
            </p>
            {buttonsOf(m).length > 0 && (
              <p className="mt-2 flex flex-wrap gap-1">
                {buttonsOf(m).map((b) => (
                  <span
                    key={b.id}
                    className="px-2 py-0.5 rounded-full bg-surface text-[11px] font-semibold text-primary-700 ring-1 ring-slate-200"
                  >
                    {b.label}
                  </span>
                ))}
              </p>
            )}
          </div>
          <dl className="divide-y divide-slate-100">
            <Row label="Direction">{m.direction === 'in' ? 'From the recipient' : 'Sent'}</Row>
            <Row label="Recipient">{recipientText(m)}</Row>
            <Row label="Purpose">
              {intentLabel(m)}
              {m.template_key && m.template_key !== m.intent ? ` · ${m.template_key}` : ''}
              {m.language ? ` · ${m.language === 'af' ? 'Afrikaans' : 'English'}` : ''}
            </Row>
            <Row label="Created">{fmtDateTime(m.created_at)}</Row>
            <Row label="State since">{fmtDateTime(m.state_at)}</Row>
            <Row label="Attempts">{m.attempts ?? 0}</Row>
            {m.error && <Row label="Error">{<span className="text-red-700">{m.error}</span>}</Row>}
            <Row label="Cost">
              {cost !== null ? fmtZar(cost) : '—'}
              {m.cost && typeof m.cost === 'object' && m.cost.units
                ? ` · ${m.cost.units} unit${m.cost.units === 1 ? '' : 's'}`
                : ''}
              {m.cost?.category ? ` · ${m.cost.category}` : ''}
              {m.cost?.rate_card_version ? ` · rate card ${m.cost.rate_card_version}` : ''}
            </Row>
            {m.provider_message_id && (
              <Row label="Provider id">
                {<span className="font-mono text-xs">{m.provider_message_id}</span>}
              </Row>
            )}
            {(subject.trip_id || subject.deviation_id || subject.event_id || subject.rule_id) && (
              <Row label="About">
                <span className="flex flex-col gap-0.5">
                  {subject.trip_id && (
                    <Link
                      to={`/lexistar/trips/${subject.trip_id}`}
                      className="text-primary-700 hover:underline"
                    >
                      The trip
                    </Link>
                  )}
                  {subject.deviation_id && (
                    <Link to="/lexistar/approvals" className="text-primary-700 hover:underline">
                      The deviation (approvals)
                    </Link>
                  )}
                  {subject.event_id && (
                    <span className="font-mono text-xs">event {subject.event_id}</span>
                  )}
                  {subject.rule_id && (
                    <span className="text-xs text-slate-600">sent by a notification rule</span>
                  )}
                  {subject.escalation_tier && (
                    <span className="text-xs text-amber-800">
                      escalation tier {subject.escalation_tier}
                    </span>
                  )}
                </span>
              </Row>
            )}
          </dl>
          {(m.fallback_of || chain.length > 0) && (
            <section aria-label="Fallback chain">
              <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">
                Fallback chain
              </h3>
              {m.fallback_of && (
                <p className="text-sm text-slate-700 mb-2">
                  This message is a fallback after another channel failed.
                </p>
              )}
              <ol className="space-y-1.5">
                {chain.map((c) => (
                  <li key={c.id} className="flex items-center gap-2 text-sm">
                    <ChannelBadge channel={c.channel} />
                    <StateChip state={c.state} />
                    <span className="text-xs text-slate-500 tabular-nums">
                      {fmtDateTime(c.created_at)}
                    </span>
                    {c.error && <span className="text-xs text-red-700 truncate">{c.error}</span>}
                  </li>
                ))}
              </ol>
            </section>
          )}
        </div>
      </aside>
    </div>
  );
}
